import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { ROLE_LABELS, formatDateTime } from "@/lib/constants";
import { Bell, Menu, Check, KeyRound, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import ChangePasswordDialog from "./ChangePasswordDialog";

/**
 * Portals a page's chrome into the shared Topbar.
 *
 *   slot="actions" (default) — right side, just before the notification bell
 *   slot="title"             — left side, immediately after the page title
 *
 * Waits for the slot node to exist on mount, since Topbar renders above the
 * page in the tree.
 */
export function TopbarPortal({ children, slot = "actions" }) {
  const [node, setNode] = useState(null);
  useEffect(() => {
    setNode(document.getElementById(`topbar-${slot}-slot`));
  }, [slot]);
  return node ? createPortal(children, node) : null;
}

export default function Topbar({ title, onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState({ unread: 0, items: [] });
  const [pwOpen, setPwOpen] = useState(false);

  const loadNotifs = useCallback(() => {
    api.get("/notifications").then((res) => setNotifs(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadNotifs();
    // Slow poll kept as a fallback; SSE makes it effectively real-time.
    const t = setInterval(loadNotifs, 60000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  // Real-time: refresh the bell the instant a notification is created for me.
  useLiveEvents((event) => {
    if (event.type === "notification" && event.user_id === user?.id) {
      loadNotifs();
    }
  });

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    loadNotifs();
  };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-navy/10 bg-cream/80 px-4 backdrop-blur-md md:px-8">
      <div className="flex items-center gap-3">
        <button onClick={onMenuClick} className="rounded-lg p-2 text-navy hover:bg-navy/5 md:hidden" data-testid="mobile-menu-btn">
          <Menu className="h-5 w-5" />
        </button>
        <h1 className="font-display text-xl font-semibold text-navy md:text-2xl" data-testid="page-title">{title}</h1>
        {/* Slot for view switchers that belong beside the page title (e.g. the
            Appointments List/Waiting toggle). */}
        <div id="topbar-title-slot" className="flex items-center gap-2" />
      </div>

      <div className="flex items-center gap-3">
        {/* Slot pages can portal action UI into (e.g. the Leads search bar),
            rendered just before the notification bell. */}
        <div id="topbar-actions-slot" className="flex items-center gap-2" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="relative rounded-full p-2 text-navy transition-colors hover:bg-navy/5" data-testid="notifications-btn">
              <Bell className="h-5 w-5" />
              {notifs.unread > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-white">
                  {notifs.unread}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <p className="font-display font-semibold text-navy">Notifications</p>
              {notifs.unread > 0 && (
                <button onClick={markAllRead} className="flex items-center gap-1 text-xs font-medium text-gold hover:underline" data-testid="mark-all-read-btn">
                  <Check className="h-3 w-3" /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifs.items.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet</p>
              ) : (
                notifs.items.map((n) => (
                  <div key={n.id} className={`border-b px-4 py-3 ${n.read ? "" : "bg-nude/40"}`}>
                    <p className="text-sm font-medium text-ink">{n.title}</p>
                    <p className="text-xs text-muted-foreground">{n.message}</p>
                    <p className="mt-1 text-[10px] text-muted-foreground">{formatDateTime(n.created_at)}</p>
                  </div>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hidden items-center gap-3 rounded-full bg-white py-1.5 pl-3 pr-1.5 shadow-sm transition-colors hover:bg-nude/40 sm:flex" data-testid="user-menu-btn">
              <div className="text-right">
                <p className="text-xs font-semibold leading-none text-ink">{user?.name}</p>
                <p className="text-[10px] text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
              </div>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-sm font-semibold text-white">
                {user?.name?.charAt(0)}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-btn">
              <KeyRound className="mr-2 h-4 w-4" /> Change password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="topbar-signout-btn">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      </div>
    </header>
  );
}
